// Session-expiry handling for the server-rendered (Turbo / Stimulus) layer.
//
// When a session ends (the 3h `sessions:cleanup` cron destroys the row, or the
// user logs out elsewhere) the browser still holds the permanent `session_id`
// cookie, so the next request resolves to no session. The server responds by
// either redirecting a navigational/Turbo request to the login page (302 →
// /session/new) or returning `head :unauthorized` (401) for a JSON request.
//
// Without this, those failures can surface as the generic branded error modal
// ("Something went wrong / Our team has been notified") — which is wrong: nothing
// went wrong, the session simply expired. This interceptor turns any such Turbo
// response into a clean redirect to the login page (with a return_to), matching
// what the React SPA already does in app/frontend/react/lib/api.ts + navigation.ts.

let redirecting = false

// Indirection for the hard redirect — jsdom's window.location is unforgeable,
// so tests stub this object instead (same pattern as global_error_modal.js).
export const navigation = {
  assign: (url) => window.location.assign(url),
}

// True once a session-expiry redirect is in flight. The global error modal
// checks this so it never flashes "Something went wrong" while we navigate away.
export function isSessionExpiryRedirecting() {
  return redirecting
}

export const SIGN_IN_PATH = '/session/new'

export function redirectToLoginForExpiredSession() {
  if (redirecting) return
  redirecting = true
  const returnTo = window.location.pathname + window.location.search
  navigation.assign(`${SIGN_IN_PATH}?return_to=${encodeURIComponent(returnTo)}`)
}

// Whether a response to a request.js call (or a raw fetch Response) means the
// session ended (#14273 item 7): a JSON request answers 401, a form-shaped
// request is 302'd to the sign-in page — and fetch FOLLOWS that redirect, so
// the caller holds a 200 HTML page whose only tell is `redirected` plus the
// final URL. A followed redirect anywhere else (the cart page after a create
// POST) is the normal flow and is not expiry. request.js wraps the raw
// Response as `response.response` and exposes `statusCode`; both shapes read.
export function sessionExpiredResponse(response) {
  if (!response) return false
  const status = response.statusCode ?? response.status
  if (status === 401) return true
  if (!response.redirected) return false
  const url = response.response?.url ?? response.url
  try {
    return new URL(url, window.location.href).pathname === SIGN_IN_PATH
  } catch {
    return false
  }
}

// A network-level fetch failure (the request never received an HTTP response)
// is ambiguous: the most common cause in practice is a laptop waking from
// sleep — background refetches fire before the network stack is usable AND the
// session usually expired overnight, so the right destination is the login
// page, not an error modal. Probing a featherweight authenticated endpoint
// disambiguates the three cases for the caller (global_error_modal.js).
export const SESSION_PROBE_PATH = '/session/probe'
export const SESSION_PROBE_RETRY_DELAY_MS = 2000
export const SESSION_PROBE_TIMEOUT_MS = 5000

export const SESSION_PROBE = {
  AUTHENTICATED: 'authenticated',
  UNAUTHENTICATED: 'unauthenticated',
  UNREACHABLE: 'unreachable',
}

async function probeSessionOnce() {
  // A hung connection (packets silently dropped, no RST) can leave fetch
  // neither resolving nor rejecting. The caller's in-flight guard would then
  // stay latched forever, permanently disabling network-failure handling for
  // the rest of the page life — so the probe must always settle. The race
  // guarantees that even if the fetch implementation ignores the abort.
  const controller = new AbortController()
  let timeoutId
  const timedOut = new Promise((_, reject) => {
    timeoutId = setTimeout(() => {
      controller.abort()
      reject(new Error('session probe timed out'))
    }, SESSION_PROBE_TIMEOUT_MS)
  })
  try {
    const response = await Promise.race([
      fetch(SESSION_PROBE_PATH, {
        headers: { 'Accept': 'application/json' },
        credentials: 'same-origin',
        // A liveness probe must reflect current state, never a cached one.
        cache: 'no-store',
        // Without 'manual', fetch would silently follow a 302 to the login page
        // and report the login HTML as a 200 — an opaque redirect (status 0) is
        // the session-expired signal for servers that redirect instead of 401ing.
        redirect: 'manual',
        signal: controller.signal,
      }),
      timedOut,
    ])
    if (response.status === 401 || response.type === 'opaqueredirect' || response.status === 0) {
      return SESSION_PROBE.UNAUTHENTICATED
    }
    // A non-auth failure (5xx) means the server is up but struggling — that is
    // modal territory, same as an unreachable server.
    return response.ok ? SESSION_PROBE.AUTHENTICATED : SESSION_PROBE.UNREACHABLE
  } finally {
    clearTimeout(timeoutId)
  }
}

// One delayed retry rides out the wake-from-sleep race where even the probe
// fires before the network is back.
export async function classifySessionAfterNetworkFailure({ retryDelayMs = SESSION_PROBE_RETRY_DELAY_MS } = {}) {
  try {
    return await probeSessionOnce()
  } catch {
    await new Promise((resolve) => setTimeout(resolve, retryDelayMs))
  }
  try {
    return await probeSessionOnce()
  } catch {
    return SESSION_PROBE.UNREACHABLE
  }
}

// ---------------------------------------------------------------------------
// Post-outage session recovery watch: after classifySessionAfterNetworkFailure
// returns UNREACHABLE, the session state is UNKNOWN — the probe never reached
// the server to ask. A wake-from-sleep network outage routinely outlives the
// classifier's ~7s window, and most of the time nothing is actually wrong (the
// session merely timed out overnight). Keep probing (poll plus the browser's
// `online` event) until one probe settles: expired → login page, alive → stop
// and leave the page alone. Bounded so a genuine long outage doesn't poll
// forever — the caller (global_error_modal.js) shows the error modal only when
// the watch exhausts with UNREACHABLE, i.e. a sustained, confirmed outage;
// beyond that the 2-minute watchdog below still owns eventual recovery. Hidden
// tabs don't burn probes (same policy as the watchdog); the visibilitychange
// listener resumes the watch on surface.
// ---------------------------------------------------------------------------
export const SESSION_RECOVERY_POLL_MS = 5000
export const SESSION_RECOVERY_MAX_ATTEMPTS = 24

let recoveryWatchActive = false

export function watchSessionRecoveryAfterOutage({
  pollMs = SESSION_RECOVERY_POLL_MS,
  maxAttempts = SESSION_RECOVERY_MAX_ATTEMPTS,
} = {}) {
  // One watch at a time — repeated network failures while the modal is up
  // (query retries) must not stack parallel probe loops.
  if (recoveryWatchActive || redirecting) return null
  recoveryWatchActive = true

  return new Promise((resolve) => {
    let attempts = 0
    let timerId = null
    let probing = false
    let settled = false

    const finish = (verdict) => {
      if (settled) return
      settled = true
      clearTimeout(timerId)
      window.removeEventListener('online', probeNow)
      document.removeEventListener('visibilitychange', onVisibilityChange)
      recoveryWatchActive = false
      if (verdict === SESSION_PROBE.UNAUTHENTICATED) {
        redirectToLoginForExpiredSession()
      }
      resolve(verdict)
    }

    const probeNow = async () => {
      if (settled || probing) return
      // Don't burn requests on a tab nobody is looking at (same policy as the
      // watchdog). Deliberately not rescheduled — the visibilitychange listener
      // resumes the watch the moment the tab surfaces.
      if (document.hidden) return
      probing = true
      clearTimeout(timerId)
      const startedAt = Date.now()
      let verdict = SESSION_PROBE.UNREACHABLE
      try {
        verdict = await probeSessionOnce()
      } catch {
        // Still unreachable — wait for the network to come back.
      }
      probing = false
      if (settled) return
      if (verdict === SESSION_PROBE.UNREACHABLE) {
        attempts += 1
        if (attempts >= maxAttempts) {
          finish(SESSION_PROBE.UNREACHABLE)
          return
        }
        // Schedule relative to when THIS attempt started: a probe that hung to
        // its internal SESSION_PROBE_TIMEOUT_MS already consumed the interval,
        // so the next attempt fires immediately instead of stacking timeout on
        // top of pollMs (which would double the intended give-up window in
        // exactly the hung-connection case this watch exists for).
        timerId = setTimeout(probeNow, Math.max(0, pollMs - (Date.now() - startedAt)))
        return
      }
      finish(verdict)
    }

    const onVisibilityChange = () => {
      if (!document.hidden) probeNow()
    }

    window.addEventListener('online', probeNow)
    document.addEventListener('visibilitychange', onVisibilityChange)
    timerId = setTimeout(probeNow, pollMs)
  })
}

// ---------------------------------------------------------------------------
// Session timeout watchdog (#9769): make an OPEN page react when its session
// dies, instead of displaying lead data until the user happens to click.
//
// The 3h `sessions:cleanup` cron destroys idle session rows server-side, but a
// tab left open keeps rendering whatever it last loaded — on a shared screen
// that is customer PII visible to anyone walking by. The watchdog polls the
// featherweight /session/probe (which deliberately skips `update_session`, so
// polling can never keep a session alive) and hard-redirects to the login page
// the moment the session is gone. return_to brings the user straight back
// after signing in.
//
// Deliberately NOT an outage detector: a probe that errors or 5xxes is
// inconclusive and does nothing — the next tick will see clearer weather.
// ---------------------------------------------------------------------------
export const SESSION_WATCHDOG_INTERVAL_MS = 2 * 60 * 1000

let watchdogInitialized = false
let watchdogChecking = false

function onSessionPage() {
  return /^\/session(\/|$)/.test(window.location.pathname)
}

async function watchdogCheck() {
  // hidden: don't burn requests for tabs nobody is looking at — the
  // visibilitychange listener below covers them the moment they surface.
  if (watchdogChecking || redirecting || document.hidden || onSessionPage()) return

  watchdogChecking = true
  try {
    if ((await probeSessionOnce()) === SESSION_PROBE.UNAUTHENTICATED) {
      redirectToLoginForExpiredSession()
    }
  } catch {
    // Network flake (wake-from-sleep, flaky wifi) — inconclusive, retry next tick.
  } finally {
    watchdogChecking = false
  }
}

export function initSessionTimeoutWatchdog() {
  if (watchdogInitialized) return
  // The layout stamps this only when the page was rendered for a signed-in
  // session — without the gate, a logged-out visitor on a public page would be
  // shoved to the login screen by the first probe.
  if (document.documentElement.dataset.sessionAuthenticated !== 'true') return

  watchdogInitialized = true
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) watchdogCheck()
  })
  setInterval(watchdogCheck, SESSION_WATCHDOG_INTERVAL_MS)
}

// A Turbo response signals an expired session when it is a 401, or when the
// request was bounced to the login page (302 → /session/new, which `fetch`
// follows, leaving a redirected response at the login URL).
function isSessionExpiry(fetchResponse) {
  if (!fetchResponse) return false
  if (fetchResponse.statusCode === 401) return true
  const url = fetchResponse.response?.url || ''
  return Boolean(fetchResponse.redirected && /\/session\/new(\?|$)/.test(url))
}

let initialized = false

export function initSessionExpiryInterceptor() {
  if (initialized) return
  initialized = true
  document.addEventListener('turbo:before-fetch-response', (event) => {
    if (isSessionExpiry(event.detail?.fetchResponse)) {
      // Stop Turbo from rendering the login page into a frame / replacing the
      // body with it; do a top-level redirect that preserves where to return.
      event.preventDefault()
      redirectToLoginForExpiredSession()
    }
  })
}
